/**
 * Minimal POST-able SSE consumer.
 *
 * The native `EventSource` API only does GET, so we hand-roll a stream
 * parser over `fetch` for endpoints like `/menu/generate/stream` that
 * take a JSON body.
 *
 * Yields parsed payloads as they arrive. By default each frame's `data`
 * is JSON-parsed and the caller narrows it to a concrete event type; pass
 * `parse` to handle event names or non-JSON sentinels such as `[DONE]`.
 */

/** One raw SSE frame: its `event:` name (default `message`) and `data`. */
export interface SseFrame {
  event: string;
  data: string;
}

export interface SsePostOptions<T = unknown> {
  url: string;
  body: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Map a raw frame to a value; return `null` to skip the frame.
   *  Defaults to JSON-parsing `data` (unparseable frames are skipped). */
  parse?: (frame: SseFrame) => T | null;
}

export async function* sseFromPost<T>(
  opts: SsePostOptions<T>,
): AsyncGenerator<T, void, undefined> {
  const parse = opts.parse ?? parseJsonFrame<T>;
  const response = await fetch(opts.url, {
    method: "POST",
    body: JSON.stringify(opts.body),
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...opts.headers,
    },
    signal: opts.signal,
  });

  if (!response.ok || !response.body) {
    throw new SseError(response.status, await readErrorBody(response));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        // Drain anything left in the buffer.
        if (buffer.trim()) {
          const ev = decodeFrame(buffer, parse);
          if (ev !== null) yield ev;
        }
        return;
      }
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      // SSE frames are separated by a blank line — `\n\n` on every server
      // I've seen, but accept `\r\n\r\n` too.
      while ((sep = nextFrameBoundary(buffer)) >= 0) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep).replace(/^(\r?\n){2}/, "");
        const ev = decodeFrame(raw, parse);
        if (ev !== null) yield ev;
      }
    }
  } finally {
    void reader.cancel().catch(() => {});
  }
}

/**
 * Read an error response body exactly once. A body stream can only be
 * consumed a single time, so calling `.json()` then `.text()` on failure
 * throws "Body is unusable" and masks the real HTTP status. Read the raw
 * text, then try to parse it as JSON (FastAPI's `{"detail": ...}`).
 * Returns `null` for an empty body.
 */
async function readErrorBody(response: Response): Promise<unknown> {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return null;
  }
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function nextFrameBoundary(buf: string): number {
  const a = buf.indexOf("\n\n");
  const b = buf.indexOf("\r\n\r\n");
  if (a === -1) return b;
  if (b === -1) return a;
  return Math.min(a, b);
}

/** Split a raw frame into its event name and data. Only the single
 *  optional space after `data:` is stripped — token whitespace is
 *  significant (audit H12). Returns `null` for a frame without data. */
function splitFrame(raw: string): SseFrame | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim() || "message";
    } else if (line.startsWith("data:")) {
      const value = line.slice(5);
      dataLines.push(value.startsWith(" ") ? value.slice(1) : value);
    }
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

function decodeFrame<T>(
  raw: string,
  parse: (frame: SseFrame) => T | null,
): T | null {
  const frame = splitFrame(raw);
  return frame ? parse(frame) : null;
}

function parseJsonFrame<T>(frame: SseFrame): T | null {
  try {
    return JSON.parse(frame.data) as T;
  } catch {
    return null;
  }
}

export class SseError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`SSE error ${status}`);
    this.name = "SseError";
  }
}
